import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";

interface TruncatedTextProps {
  text: string;
  maxWidth?: string;
}

const TruncatedText: React.FC<TruncatedTextProps> = ({ text, maxWidth = "100%" }) => {
  const [show, setShow] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number; above: boolean; arrowLeft: number } | null>(null);
  const ref = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const TOOLTIP_MAX_WIDTH = 300; 

  const isOverflowing = () =>
    ref.current ? ref.current.scrollWidth > ref.current.clientWidth : false;

  const handleMouseEnter = () => {
    if (!isOverflowing() || !ref.current) return;
    setShow(true);
  };

  const handleMouseLeave = () => {
    setShow(false);
    setPos(null);
  };

  useEffect(() => {
    if (!show || !ref.current || !tooltipRef.current) return;

    const rect = ref.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    const left = Math.max(8, Math.min(rect.left, vw - TOOLTIP_MAX_WIDTH - 16));
    const arrowLeft = Math.max(
      12,
      Math.min(rect.left - left + rect.width / 2 - 6, TOOLTIP_MAX_WIDTH - 18)
    );

    // Determine if we should place it above or below based on actual height
    let above = rect.top > tooltipRect.height + 16;
    if (!above && rect.bottom + tooltipRect.height + 16 > vh) {
      // If neither has enough space, choose the side with more space
      above = rect.top > vh - rect.bottom;
    }

    setPos({
      top: above ? rect.top - 8 : rect.bottom + 8,
      left,
      above,
      arrowLeft,
    });
  }, [show, text]);

  return (
    <span style={{ position: "relative", display: "inline-block", maxWidth, verticalAlign: "bottom" }}>
      <span
        ref={ref}
        style={{
          display: "block",
          overflow: "hidden",
          whiteSpace: "nowrap",
          textOverflow: "ellipsis",
          cursor: "default",
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {text}
      </span>

      {show &&
        createPortal(
          <div
            ref={tooltipRef}
            style={{
              position: "fixed",
              backgroundColor: "#1e293b",
              color: "#f8fafc",
              padding: "8px 12px",
              borderRadius: "8px",
              fontSize: "12.5px",
              lineHeight: "1.6",
              maxWidth: `${TOOLTIP_MAX_WIDTH}px`,
              wordBreak: "break-word",
              whiteSpace: "normal",
              zIndex: 9999,
              boxShadow: "0 6px 20px rgba(0,0,0,0.18)",
              pointerEvents: "none",
              visibility: pos ? "visible" : "hidden",
              left: pos ? `${pos.left}px` : "0px",
              ...(pos
                ? pos.above
                  ? { top: `${pos.top}px`, transform: "translateY(-100%)" }
                  : { top: `${pos.top}px`, transform: "none" }
                : { top: "0px", transform: "none" }),
            }}
          >
            {text}
            {pos && (
              <div
                style={{
                  position: "absolute",
                  ...(pos.above
                    ? { top: "100%", borderTop: "6px solid #1e293b" }
                    : { bottom: "100%", borderBottom: "6px solid #1e293b" }),
                  left: `${pos.arrowLeft}px`,
                  width: 0,
                  height: 0,
                  borderLeft: "6px solid transparent",
                  borderRight: "6px solid transparent",
                }}
              />
            )}
          </div>,
          document.body
        )
      }
    </span>
  );
};

export default TruncatedText;